import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { graphqlMutation, graphqlQuery } from '../utils/graphqlApi';
import {
  updateRunbook,
  refreshImageUrls as refreshImageUrlsMutation,
  uploadStepImage as uploadStepImageMutation,
  deleteStepImage as deleteStepImageMutation,
  generateImageUploadUrl as generateImageUploadUrlMutation,
  confirmImageUpload as confirmImageUploadMutation,
} from '../graphql/mutations';
import { getRunbook } from '../graphql/queries';
import useUserPermissions from '../hooks/useUserPermissions';
import { reorderStep } from '../utils/stepReorder';

const DIRECT_UPLOAD_THRESHOLD = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif,image/bmp,image/webp';

function EditRunbook() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const { canModify, isLoading } = useUserPermissions();
  
  const [runbook, setRunbook] = useState(null);
  const [editedRunbook, setEditedRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [highlightedStep, setHighlightedStep] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [uploadingSteps, setUploadingSteps] = useState({});
  const [deletingSteps, setDeletingSteps] = useState({});
  const fileInputRefs = useRef({});

  const getStepImageUrl = (step) => step.image_url || step.imageUrl || step.URL || step.url || '';
  const getStepImageKey = (step) => step.image_s3_key || step.imageS3Key || step.s3_key || step.s3Key || '';
  const getStepNumber = (step, index) => step.step_number || step['Step Number'] || String(index + 1);
  const getStepDescription = (step) => step.description || step.step_description || step.Description || '';

  const refreshRunbookImageUrls = useCallback(async (steps) => {
    if (!steps || steps.length === 0) return;
    try {
      const stepsWithExistingImageUrls = steps.some((step) => getStepImageUrl(step));
      const runbookSteps = steps
        .map((step, index) => ({
          step_number: getStepNumber(step, index),
          description: getStepDescription(step),
          image_s3_key: getStepImageKey(step),
        }))
        .filter((s) => s.image_s3_key);

      if (runbookSteps.length === 0) {
        if (!stepsWithExistingImageUrls) {
          console.info('[EditRunbook][RefreshImageUrls] No image references found in steps. Skipping refresh.');
        }
        return;
      }

      const response = await graphqlMutation(refreshImageUrlsMutation, {
        input: {
          body: JSON.stringify({ runbook_steps: runbookSteps }),
        },
      });

      console.log('[EditRunbook][RefreshImageUrls] Response:', response);

      const lambdaResponse = response.refreshImageUrls;
      let data =
        typeof lambdaResponse.body === 'string'
          ? JSON.parse(lambdaResponse.body)
          : lambdaResponse.body;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      console.log('[EditRunbook][RefreshImageUrls] Parsed data:', data);

      const urlMap = {};
      (data.runbook_steps || []).forEach((rs) => {
        if (rs.step_number && (rs.image_url || rs.imageUrl)) {
          urlMap[rs.step_number] = rs.image_url || rs.imageUrl;
        }
      });
      setImageUrls(urlMap);
    } catch (err) {
      console.warn('[EditRunbook][RefreshImageUrls] Error refreshing URLs:', err.message);
    }
  }, []);

  // Redirect users without modify permissions
  useEffect(() => {
    if (!isLoading && !canModify()) {
      alert('You do not have permission to edit runbooks. Redirecting to view mode.');
      navigate(`/view-runbook/${id}`);
      return;
    }
  }, [canModify, isLoading, navigate, id]);

  useEffect(() => {
    const fetchRunbookData = async () => {
      try {
        setLoading(true);
        const keyword = decodeURIComponent(id);
        const result = await graphqlQuery(getRunbook, { keyword });
        const data = result.getRunbook;
        console.log('[EditRunbook] Fetched runbook via AppSync:', data);
        
        setRunbook(data);
        setEditedRunbook(JSON.parse(JSON.stringify(data))); // Deep copy
        setLoading(false);
        if (data.runbook_steps && data.runbook_steps.length > 0) {
          refreshRunbookImageUrls(data.runbook_steps);
        }
      } catch (error) {
        console.error('Error fetching runbook data:', error);
        setError(error.message || 'Failed to load runbook');
        setLoading(false);
      }
    };
    fetchRunbookData();
  }, [id, refreshRunbookImageUrls]);

  const handleStepChange = (index, field, value) => {
    const currentSteps = editedRunbook.runbook_steps || [];
    const newSteps = [...currentSteps];
    newSteps[index] = {
      ...newSteps[index],
      [field]: field === 'step_number' ? parseInt(value) || 0 : value
    };
    setEditedRunbook(prev => ({
      ...prev,
      runbook_steps: newSteps
    }));
  };

  const handleAddStep = () => {
    const currentSteps = editedRunbook.runbook_steps || [];
    const newStepNumber = currentSteps.length + 1;
    const newSteps = [
      ...currentSteps,
      {
        step_number: newStepNumber,
        description: ''
      }
    ];
    setEditedRunbook(prev => ({
      ...prev,
      runbook_steps: newSteps
    }));
  };

  const handleDeleteStep = (index) => {
    if (!window.confirm('Are you sure you want to delete this step?')) {
      return;
    }

    const currentSteps = editedRunbook.runbook_steps || [];
    if (currentSteps.length === 0) return;
    
    const newSteps = currentSteps.filter((_, idx) => idx !== index);
    
    // Renumber steps
    const renumberedSteps = newSteps.map((step, idx) => ({
      ...step,
      step_number: idx + 1
    }));
    
    setEditedRunbook(prev => ({
      ...prev,
      runbook_steps: renumberedSteps
    }));
    
    console.log('Step deleted. Steps renumbered.');
  };

  const handleImageUpload = async (index) => {
    const file = fileInputRefs.current[index]?.files?.[0];
    if (!file) return;

    const stepNumber = editedRunbook.runbook_steps[index].step_number;
    const keyword = decodeURIComponent(id);

    setUploadingSteps((prev) => ({ ...prev, [stepNumber]: true }));
    try {
      let imageUrl;
      let s3Key;

      if (file.size >= DIRECT_UPLOAD_THRESHOLD) {
        // Large file: use presigned URL direct upload via AppSync
        const urlResult = await graphqlMutation(generateImageUploadUrlMutation, {
          input: { keyword, stepNumber, fileName: file.name },
        });
        const urlData = urlResult.generateImageUploadUrl;

        await fetch(urlData.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': urlData.contentType },
          body: file,
        });

        const confirmResult = await graphqlMutation(confirmImageUploadMutation, {
          input: { keyword, stepNumber, s3Key: urlData.s3Key },
        });
        imageUrl = confirmResult.confirmImageUpload.imageUrl;
        s3Key = urlData.s3Key;
      } else {
        // Small file: base64 inline upload via AppSync
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const result = await graphqlMutation(uploadStepImageMutation, {
          input: { keyword, stepNumber, image: base64, fileName: file.name },
        });
        imageUrl = result.uploadStepImage.imageUrl;
        s3Key = result.uploadStepImage.s3Key;
      }

      // Update the step with new image info
      const newSteps = [...editedRunbook.runbook_steps];
      newSteps[index] = { ...newSteps[index], image_s3_key: s3Key || newSteps[index].image_s3_key, image_url: imageUrl };
      setEditedRunbook((prev) => ({ ...prev, runbook_steps: newSteps }));
      setImageUrls((prev) => ({ ...prev, [stepNumber]: imageUrl }));
      console.log(`[EditRunbook] Image uploaded for step ${stepNumber}`);
    } catch (err) {
      console.error('[EditRunbook] Image upload failed:', err);
      alert('Image upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingSteps((prev) => ({ ...prev, [stepNumber]: false }));
      if (fileInputRefs.current[index]) fileInputRefs.current[index].value = '';
    }
  };

  const handleImageDelete = async (index) => {
    if (!window.confirm('Are you sure you want to remove this image?')) return;

    const stepNumber = editedRunbook.runbook_steps[index].step_number;
    const keyword = decodeURIComponent(id);

    setDeletingSteps((prev) => ({ ...prev, [stepNumber]: true }));
    try {
      await graphqlMutation(deleteStepImageMutation, {
        input: { keyword, stepNumber },
      });

      const newSteps = [...editedRunbook.runbook_steps];
      newSteps[index] = { ...newSteps[index], image_s3_key: null, image_url: null };
      setEditedRunbook((prev) => ({ ...prev, runbook_steps: newSteps }));
      setImageUrls((prev) => {
        const updated = { ...prev };
        delete updated[stepNumber];
        return updated;
      });
      console.log(`[EditRunbook] Image deleted for step ${stepNumber}`);
    } catch (err) {
      console.error('[EditRunbook] Image delete failed:', err);
      alert('Image delete failed: ' + (err.message || 'Unknown error'));
    } finally {
      setDeletingSteps((prev) => ({ ...prev, [stepNumber]: false }));
    }
  };

  const handleMoveStep = (index, direction) => {
    const currentSteps = editedRunbook.runbook_steps || [];
    const result = reorderStep(currentSteps, {}, index, direction);
    if (!result) return;

    setEditedRunbook(prev => ({
      ...prev,
      runbook_steps: result.steps
    }));

    // Highlight the moved step at its new position
    setHighlightedStep(result.newIndex + 1);
    setTimeout(() => setHighlightedStep(null), 1200);

    console.log('[REORDER] Moved step from position ' + (index + 1) + ' ' + direction + ' to position ' + (result.newIndex + 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Get the current user's email for modified_by
    let userEmail = '';
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken;
      if (idToken) {
        const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
        userEmail = payload.email || payload.preferred_username || payload['cognito:username'] || '';
      }
    } catch (err) {
      console.warn('Could not retrieve user email for modified_by:', err);
    }

    const input = {
      modified_by: userEmail,
      runbook_steps: (editedRunbook.runbook_steps || []).map(step => ({
        step_number: step.step_number,
        description: step.description || '',
        image_s3_key: step.image_s3_key || null
      }))
    };
    
    console.log('[EDIT] Submitting runbook update...', input);
    
    try {
      setSaving(true);
      const keyword = decodeURIComponent(id);
      const response = await graphqlMutation(updateRunbook, { keyword, input });
      console.log('[EDIT] Update successful:', response);
      alert('Runbook updated successfully!');
      navigate('/runbook_list');
    } catch (error) {
      console.error('[EDIT] Error updating runbook:', error);
      alert('Failed to update runbook: ' + (error.message || 'Unknown error'));
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const hasUnsavedChanges = JSON.stringify(runbook) !== JSON.stringify(editedRunbook);
    
    if (hasUnsavedChanges) {
      const confirmCancel = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      );
      if (!confirmCancel) return;
    }
    
    navigate('/runbook_list');
  };

  if (loading || isLoading) {
    return (
      <div className="page-container">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading runbook...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button className="kone-btn kone-btn-primary" onClick={() => navigate('/runbook_list')}>
            Back to Runbook List
          </button>
        </div>
      </div>
    );
  }

  if (!editedRunbook) {
    return <div className="page-container"><p>Runbook not found</p></div>;
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="kone-section-title">Edit Runbook</h2>
        <button 
          className="kone-btn kone-btn-secondary"
          onClick={() => navigate('/runbook_list')}
          disabled={saving}
        >
          Back to Runbook List
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Basic Information</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="kone-label">Keyword</label>
                <input
                  type="text"
                  className="kone-input"
                  value={editedRunbook.keyword || ''}
                  readOnly
                />
              </div>
              <div className="col-md-6">
                <label className="kone-label">Tribe</label>
                <input
                  type="text"
                  className="kone-input"
                  readOnly
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="kone-label">Is Automation</label>
                <input
                  type="text"
                  className="kone-input"
                  value={editedRunbook.is_automation ? 'Yes' : 'No'}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Resolution Steps ({editedRunbook.runbook_steps?.length || 0})</h5>
            <button
              type="button"
              className="kone-btn kone-btn-primary btn-sm"
              onClick={handleAddStep}
            >
              Add Step
            </button>
          </div>
          <div className="card-body">
            {editedRunbook.runbook_steps && editedRunbook.runbook_steps.length > 0 ? (
              editedRunbook.runbook_steps.map((step, index) => (
                <div
                  key={index}
                  className={`card mb-3 step-card-reorderable ${highlightedStep === step.step_number ? 'step-card-highlight' : ''}`}
                >
                  <div className="card-body">
                    <div className="row mb-2">
                      <div className="col-md-2">
                        <label className="kone-label">Step #</label>
                        <input
                          type="number"
                          className="kone-input"
                          value={step.step_number}
                          disabled
                          min="1"
                        />
                      </div>
                      <div className="col-md-10">
                        <label className="kone-label">
                          Description
                        </label>
                        <textarea
                          className="kone-input"
                          rows="3"
                          value={step.description || ''}
                          onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                          placeholder="Enter step description"
                        />
                      </div>
                    </div>

                    {/* Step Image */}
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label className="kone-label">Step Image</label>

                        {/* Upload / Change button */}
                        <input
                          type="file"
                          accept={ACCEPTED_IMAGE_TYPES}
                          ref={(el) => (fileInputRefs.current[index] = el)}
                          style={{ display: 'none' }}
                          onChange={() => handleImageUpload(index)}
                        />
                        {/* View Image button */}
                        {step.image_s3_key && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2"
                            disabled={!imageUrls[step.step_number] && !step.image_url}
                            onClick={() => window.open(imageUrls[step.step_number] || step.image_url, '_blank', 'noopener,noreferrer')}
                          >
                            <i className="bi bi-image me-1"></i>
                            {(imageUrls[step.step_number] || step.image_url) ? 'View Image' : 'Loading image...'}
                          </button>
                        )}

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          disabled={uploadingSteps[step.step_number]}
                          onClick={() => fileInputRefs.current[index]?.click()}
                        >
                          {uploadingSteps[step.step_number] ? (
                            <><span className="spinner-border spinner-border-sm me-1"></span>Uploading...</>
                          ) : step.image_s3_key ? (
                            <><i className="bi bi-arrow-repeat me-1"></i>Change Image</>
                          ) : (
                            <><i className="bi bi-upload me-1"></i>Upload Image</>
                          )}
                        </button>

                        {/* Delete button */}
                        {step.image_s3_key && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={deletingSteps[step.step_number]}
                            onClick={() => handleImageDelete(index)}
                          >
                            {deletingSteps[step.step_number] ? (
                              <><span className="spinner-border spinner-border-sm me-1"></span>Removing...</>
                            ) : (
                              <><i className="bi bi-trash me-1"></i>Remove Image</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="step-actions-row">
                      <div className="step-reorder-btn-group">
                        <button
                          type="button"
                          className="step-reorder-btn"
                          title="Move step up"
                          onClick={() => handleMoveStep(index, 'up')}
                          disabled={index === 0}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="step-reorder-btn"
                          title="Move step down"
                          onClick={() => handleMoveStep(index, 'down')}
                          disabled={index === editedRunbook.runbook_steps.length - 1}
                        >
                          ▼
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteStep(index)}
                      >
                        <i className="bi bi-trash me-1"></i>
                        Delete Step
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="alert alert-warning">
                No steps defined. Click "Add Step" to create one.
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2 mb-4">
          {canModify() && (
            <button 
              type="submit" 
              className="kone-btn kone-btn-primary" 
              disabled={saving || !editedRunbook.runbook_steps || editedRunbook.runbook_steps.length === 0}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          )}
          <button 
            type="button" 
            className="kone-btn kone-btn-secondary" 
            onClick={handleCancel}
            disabled={saving}
          >
            {canModify() ? 'Cancel' : 'Back'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditRunbook;
